import React, { useState } from "react";
import { Modal, Input, message } from "antd";
import { Loader2 } from "lucide-react";
import { mfaApi } from "../../../api/mfa";
import BackupCodesDisplay from "./BackupCodesDisplay";

interface RegenerateBackupCodesModalProps {
  open: boolean;
  userId: string;
  onClose: () => void;
}

const RegenerateBackupCodesModal: React.FC<RegenerateBackupCodesModalProps> = ({
  open,
  userId,
  onClose,
}) => {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);

  const handleClose = () => {
    setCode("");
    setBackupCodes(null);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await mfaApi.regenerateBackupCodes(userId, code);
      setBackupCodes(res.data.backupCodes);
    } catch (error) {
      message.error("Incorrect code, try again");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    message.success("Backup codes regenerated");
    handleClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={480}
      centered
      maskClosable={false}
    >
      <div className="py-2">
        {!backupCodes ? (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Regenerate backup codes
            </h2>
            <p className="text-sm text-gray-600">
              Your existing backup codes will stop working. Enter a current
              code from your authenticator app to confirm.
            </p>
            <div className="flex justify-center">
              <Input.OTP length={6} value={code} onChange={setCode} />
            </div>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={code.length !== 6 || submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium bg-primary hover:bg-primary-dark text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Regenerate"
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">
              New backup codes
            </h2>
            <BackupCodesDisplay codes={backupCodes} onDone={handleDone} />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RegenerateBackupCodesModal;
